import type { ProxyApiInstance } from '@/proxy/shared/types'
import { classifyLink } from '@/proxy/shared/link-classifier'

/**
 * Install a fallback document-level click handler that classifies <a> clicks and
 * dispatches them appropriately:
 *
 *   - host-nav   → preventDefault + stopImmediatePropagation + host.navigate(href)
 *   - child-nav  → preventDefault (subapp's router should catch via its own handler)
 *   - external   → let browser handle normally (new tab / new window)
 *   - ignore     → preventDefault (empty href, pure hash)
 *
 * Subapps using `@wippy-fe/router`'s RouterLink wrapper classify links at the
 * component level before this fallback ever fires. This remains the safety net
 * for raw `<a href>` usage.
 *
 * The host is resolved lazily via `getHost`, since early clicks may fire before
 * the proxy instance finishes its async init.
 */

let linkHandlersInstalled = false
let currentHost: ProxyApiInstance['host'] | null = null

export function setPreventClicksHost(host: ProxyApiInstance['host']) {
  currentHost = host
}

export function installPreventLinkClicks() {
  if (linkHandlersInstalled)
    return
  linkHandlersInstalled = true

  function resolveClassification(href: string | null) {
    if (currentHost?.classifyLink)
      return currentHost.classifyLink(href)
    return classifyLink(href)
  }

  function handleLinkClick(event: MouseEvent) {
    if (!(event.target instanceof HTMLElement))
      return
    const linkElement = event.target.closest('a')
    if (!linkElement)
      return
    const href = linkElement.getAttribute('href')
    const classification = resolveClassification(href)

    if (classification.kind === 'host-nav') {
      // preventDefault stops the srcdoc iframe from attempting a real
      // navigation (which would fail anyway). stopPropagation prevents
      // the subapp's own router from trying to handle it — but we use
      // stopPropagation (bubble-only), NOT stopImmediatePropagation,
      // so capture-phase analytics listeners still see the click.
      event.preventDefault()
      event.stopPropagation()
      currentHost?.navigate(classification.normalizedPath ?? classification.href)
      return
    }

    if (classification.kind === 'ignore') {
      // Empty href / pure hash / javascript: — prevent the browser default
      event.preventDefault()
    }

    // child-nav: let the subapp's RouterLink (or its own click handler)
    // decide what to do. Historically this path preventDefault'd, but
    // that broke vanilla vue-router RouterLinks because Vue Router's
    // click handler bails out when event.defaultPrevented is true.
    //
    // external: do nothing — let the browser open in a new tab naturally.
  }

  document.addEventListener('click', handleLinkClick, true)
  document.addEventListener('auxclick', handleLinkClick, true)

  document.addEventListener('mousedown', (event) => {
    if (event.button !== 1)
      return
    if (!(event.target instanceof HTMLElement))
      return
    const linkElement = event.target.closest('a')
    if (!linkElement)
      return
    const classification = resolveClassification(linkElement.getAttribute('href'))
    if (classification.kind !== 'external')
      event.preventDefault()
  }, true)
}
